import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Header = styled.header`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 28px;
  color: #333;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-height: 100px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 30px;
`;

const Button = styled.button`
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
`;

const CancelButton = styled(Button)`
  background-color: #f5f5f5;
  color: #333;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const SubmitButton = styled(Button)`
  background-color: #4285f4;
  color: white;

  &:hover {
    background-color: #3367d6;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #d93025;
  margin-top: 20px;
  padding: 10px;
  background-color: #fce8e6;
  border-radius: 4px;
`;

const SuccessMessage = styled.div`
  color: #137333;
  margin-top: 20px;
  padding: 10px;
  background-color: #e6f4ea;
  border-radius: 4px;
`;

const FileUploadContainer = styled.div`
  border: 2px dashed #ddd;
  padding: 20px;
  border-radius: 4px;
  text-align: center;
  margin-bottom: 20px;
  cursor: pointer;

  &:hover {
    border-color: #4285f4;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  color: #333;
  margin: 30px 0 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
`;

function CreateSmsEmailReportPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formData, setFormData] = useState({
    campaignName: "",
    reportDate: new Date().toISOString().split("T")[0],
    smsContent: "",
    emailHtml: "",
  });

  const [smsData, setSmsData] = useState(null);
  const [emailData, setEmailData] = useState(null);

  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSmsFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          parseCsvData(event.target.result, "sms");
        } catch (err) {
          setError(`Erreur lors de la lecture du fichier SMS: ${err.message}`);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleEmailFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          parseCsvData(event.target.result, "email");
        } catch (err) {
          setError(
            `Erreur lors de la lecture du fichier Email: ${err.message}`
          );
        }
      };
      reader.readAsText(file);
    }
  };

  const parseCsvData = (csvContent, type) => {
    // Split the content by new lines
    const lines = csvContent.split("\n");

    // Get headers (first line)
    const headers = lines[0].split(/[,;]/).map((h) => h.trim());

    // Get data (second line)
    if (lines.length < 2) {
      throw new Error("Le fichier CSV ne contient pas de données");
    }

    const dataValues = lines[1].split(/[,;]/).map((v) => v.trim());

    // Create an object from headers and values
    const data = {};
    headers.forEach((header, index) => {
      if (index < dataValues.length) {
        data[header] = dataValues[index];
      }
    });

    if (type === "sms") {
      setSmsData(data);
      setSuccess((prev) => `${prev} Données SMS chargées avec succès. `);
    } else {
      setEmailData(data);
      setSuccess((prev) => `${prev} Données Email chargées avec succès. `);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (!smsData && !emailData) {
      setError(
        "Veuillez charger au moins un fichier de données (SMS ou Email)"
      );
      setLoading(false);
      return;
    }

    try {
      const reportData = {
        ...formData,
        smsData,
        emailData,
      };

      const response = await axios.post(
        `${apiUrl}/api/reports/create-sms-email`,
        reportData
      );

      if (response.data.success) {
        setSuccess("Rapport créé avec succès!");
        setTimeout(() => {
          navigate(`/dashboard/${response.data.reportId}`);
        }, 1500);
      } else {
        setError(
          response.data.message || "Erreur lors de la création du rapport"
        );
      }
    } catch (err) {
      setError(`Erreur lors de la création du rapport: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/reports");
  };

  return (
    <PageContainer>
      <Header>
        <Title>Créer un rapport SMS/Email</Title>
        <Subtitle>
          Générez un rapport combiné pour vos campagnes SMS et Email
        </Subtitle>
      </Header>

      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Nom de la campagne</Label>
          <Input
            type="text"
            name="campaignName"
            value={formData.campaignName}
            onChange={handleChange}
            placeholder="Nom de la campagne"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Date du rapport</Label>
          <Input
            type="date"
            name="reportDate"
            value={formData.reportDate}
            onChange={handleChange}
            required
          />
        </FormGroup>

        <SectionTitle>Données SMS</SectionTitle>

        <FileUploadContainer>
          <input
            type="file"
            id="smsFileUpload"
            style={{ display: "none" }}
            accept=".csv"
            onChange={handleSmsFileUpload}
          />
          <label htmlFor="smsFileUpload" style={{ cursor: "pointer" }}>
            {smsData
              ? "Fichier SMS chargé ✓"
              : "Cliquez pour charger le fichier CSV des données SMS"}
          </label>
        </FileUploadContainer>

        {smsData && (
          <div>
            <p>Données chargées:</p>
            <ul>
              {Object.entries(smsData).map(([key, value]) => (
                <li key={key}>
                  <strong>{key}:</strong> {value}
                </li>
              ))}
            </ul>
          </div>
        )}

        <FormGroup>
          <Label>Contenu du SMS</Label>
          <Textarea
            name="smsContent"
            value={formData.smsContent}
            onChange={handleChange}
            placeholder="Entrez le contenu du SMS envoyé"
          />
        </FormGroup>

        <SectionTitle>Données Email</SectionTitle>

        <FileUploadContainer>
          <input
            type="file"
            id="emailFileUpload"
            style={{ display: "none" }}
            accept=".csv"
            onChange={handleEmailFileUpload}
          />
          <label htmlFor="emailFileUpload" style={{ cursor: "pointer" }}>
            {emailData
              ? "Fichier Email chargé ✓"
              : "Cliquez pour charger le fichier CSV des données Email"}
          </label>
        </FileUploadContainer>

        {emailData && (
          <div>
            <p>Données chargées:</p>
            <ul>
              {Object.entries(emailData).map(([key, value]) => (
                <li key={key}>
                  <strong>{key}:</strong> {value}
                </li>
              ))}
            </ul>
          </div>
        )}

        <FormGroup>
          <Label>Kit HTML de l'Email</Label>
          <Textarea
            name="emailHtml"
            value={formData.emailHtml}
            onChange={handleChange}
            placeholder="Collez ici le code HTML de l'email"
          />
        </FormGroup>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <ButtonGroup>
          <CancelButton type="button" onClick={handleCancel}>
            Annuler
          </CancelButton>
          <SubmitButton type="submit" disabled={loading}>
            {loading ? "Création en cours..." : "Créer le rapport"}
          </SubmitButton>
        </ButtonGroup>
      </form>
    </PageContainer>
  );
}

export default CreateSmsEmailReportPage;
